import React, { useState } from 'react';
import {
  Box,
  Typography,
  Rating,
  TextField,
  Alert,
  Paper,
  useTheme,
  alpha,
  Avatar,
} from '@mui/material';
import { Star as StarIcon } from '@mui/icons-material';
import GradientButton from '../../../components/buttons/GradientButton';
import { submitStorefrontAppointmentRating } from '../../../api/storefront/client';

const EmployeeRating = ({ 
  appointment, 
  onRatingSubmit, 
  disabled = false 
}) => {
  const theme = useTheme();
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (rating === 0) {
      setError('Please select a rating');
      return;
    }

    if (!comment.trim()) {
      setError('Please provide a comment');
      return;
    }

    // Debug log the entire appointment object
    console.log('Full appointment object:', appointment);

    // Validate required appointment properties
    const requiredProps = [
      'id',
      'companyId',
      'employeeId',
      'dateTime',
      'clientName',
      'clientId',
      'serviceName',
      'employeeName'
    ];

    console.log('Required properties values:', {
      id: appointment?.id,
      companyId: appointment?.companyId,
      employeeId: appointment?.employeeId,
      dateTime: appointment?.dateTime,
      clientName: appointment?.clientName,
      clientId: appointment?.clientId,
      serviceName: appointment?.serviceName,
      employeeName: appointment?.employeeName
    });

    const missingProps = requiredProps.filter(prop => !appointment?.[prop]);

    if (missingProps.length > 0) {
      console.error('Missing required appointment properties:', {
        missingProps,
        appointment,
        availableProps: Object.keys(appointment || {})
      });
      setError(`Missing required appointment data: ${missingProps.join(', ')}`);
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      console.log('Creating rating with data:', {
        rating,
        comment: comment.trim(),
        appointmentId: appointment.id,
        appointmentDate: appointment.dateTime,
        clientName: appointment.clientName,
        clientId: appointment.clientId,
        serviceName: appointment.serviceName,
        employeeName: appointment.employeeName,
        companyId: appointment.companyId,
        employeeId: appointment.employeeId,
      });

      const ratingData = {
        rating,
        comment: comment.trim(),
        appointmentId: appointment.id,
        appointmentDate: appointment.dateTime,
        clientName: appointment.clientName,
        clientId: appointment.clientId,
        serviceName: appointment.serviceName,
        employeeName: appointment.employeeName,
        createdAt: new Date().toISOString(),
      };

      const result = await submitStorefrontAppointmentRating({
        companyId: appointment.companyId,
        appointmentId: appointment.id,
        rating,
        comment: comment.trim(),
        employeeId: appointment.employeeId,
        appointmentDate: appointment.dateTime,
        clientName: appointment.clientName,
        serviceName: appointment.serviceName,
        employeeName: appointment.employeeName,
      });

      setSuccess(true);
      onRatingSubmit?.(result?.rating || ratingData);
      
      // Reset form
      setRating(0);
      setComment('');
    } catch (err) {
      console.error('Error submitting rating:', err);
      setError(`Failed to submit rating: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (success) {
    return (
      <Alert 
        severity="success"
        sx={{ 
          borderRadius: 2,
          p: 2,
          '& .MuiAlert-message': { width: '100%' }
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ color: 'success.main', mb: 1 }}>
            Thank you for your feedback!
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <Rating value={rating} readOnly size="large" 
              sx={{
                '& .MuiRating-iconFilled': {
                  color: theme.palette.warning.main,
                },
              }}
            />
            <Typography variant="subtitle1" color="text.primary">
              {rating} out of 5 stars
            </Typography>
          </Box>
        </Box>
        {comment && (
          <Typography 
            variant="body1" 
            color="text.secondary"
            sx={{ 
              mt: 2,
              fontStyle: 'italic',
              whiteSpace: 'pre-wrap',
              textAlign: 'center',
              bgcolor: alpha(theme.palette.success.main, 0.05),
              p: 2,
              borderRadius: 1,
              border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`
            }}
          >
            "{comment}"
          </Typography>
        )}
      </Alert>
    );
  }

  return (
    <Paper
      component="form"
      onSubmit={handleSubmit}
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 2,
        border: 1,
        borderColor: 'divider',
        bgcolor: theme.palette.background.default
      }}
    >
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        mb: 4 
      }}>
        <Avatar
          src={appointment.employeePhotoURL}
          alt={appointment.employeeName}
          sx={{
            width: 80,
            height: 80,
            mb: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.main,
            fontSize: '2rem',
            border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`
          }}
        >
          {appointment.employeeName?.charAt(0)}
        </Avatar>
        <Typography variant="h6" align="center" sx={{ 
          fontWeight: 600,
          background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          mb: 0.5
        }}>
          Rate Your Experience
        </Typography>
        <Typography variant="subtitle1" align="center" color="text.secondary">
          with {appointment.employeeName}
        </Typography>
      </Box>

      <Box sx={{ 
        mb: 4,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      }}>
        <Rating
          value={rating}
          onChange={(event, newValue) => {
            setRating(newValue);
            setError('');
          }}
          disabled={disabled || isSubmitting}
          size="large"
          emptyIcon={
            <StarIcon 
              style={{ opacity: 0.3 }} 
              fontSize="inherit" 
            />
          }
          sx={{
            '& .MuiRating-iconFilled': {
              color: theme.palette.warning.main,
            },
            '& .MuiRating-iconHover': {
              color: theme.palette.warning.light,
            },
            fontSize: '2.5rem',
            mb: 1
          }}
        />
        <Typography variant="body2" color="text.secondary">
          {rating > 0 ? `You've selected ${rating} star${rating !== 1 ? 's' : ''}` : 'Select your rating'}
        </Typography>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ 
          mb: 1.5, 
          fontWeight: 500,
          color: theme.palette.text.primary 
        }}>
          Share your experience
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={4}
          placeholder="Tell us what you liked or what could be improved..."
          value={comment}
          onChange={(e) => {
            setComment(e.target.value);
            setError('');
          }}
          disabled={disabled || isSubmitting}
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: 'background.paper',
              borderRadius: 2,
              '&:hover': {
                '& > fieldset': {
                  borderColor: alpha(theme.palette.primary.main, 0.5),
                }
              },
              '&.Mui-focused': {
                '& > fieldset': {
                  borderWidth: '1px',
                  borderColor: theme.palette.primary.main,
                }
              }
            }
          }}
        />
      </Box>

      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3,
            borderRadius: 2,
            '& .MuiAlert-message': {
              width: '100%'
            }
          }}
        >
          {error}
        </Alert>
      )}

      <GradientButton
        type="submit"
        variant="primary"
        disabled={disabled || isSubmitting}
        fullWidth
        size="large"
      >
        {isSubmitting ? 'Submitting...' : 'Submit Rating'}
      </GradientButton>
    </Paper>
  );
};

export default EmployeeRating; 